import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import UserProfileManager from "@/components/UserProfileManager";
import { User, Shield, Settings } from "lucide-react";

interface UserProfile {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  company?: string;
  avatarUrl?: string;
  avatarBase64?: string;
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  weeklyReports: boolean;
  criticalAlerts: boolean;
  maintenanceNotifications: boolean;
  defaultDashboard: string;
  itemsPerPage: number;
  showAdvancedFeatures: boolean;
  compactView: boolean;
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  ipWhitelist?: string[];
  lastPasswordChange?: string;
  role: string;
  permissions: string[];
  expertiseAreas: string[];
  certifications: string[];
  workingHours: {
    start: string;
    end: string;
    timezone: string;
    workDays: string[];
  };
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  screenReader: boolean;
}

const UserProfilePage: React.FC = () => {
  const [currentProfile, setCurrentProfile] = useState<UserProfile>({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@shiftleft.digital',
    phone: '(555) 123-4567',
    title: 'Senior Database Administrator',
    department: 'IT Operations',
    company: 'ShiftLeft Digital',
    theme: 'system',
    language: 'en',
    timezone: 'America/Los_Angeles',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: '12h',
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyReports: true,
    criticalAlerts: true,
    maintenanceNotifications: true,
    defaultDashboard: 'overview',
    itemsPerPage: 25,
    showAdvancedFeatures: false,
    compactView: false,
    twoFactorEnabled: true,
    sessionTimeout: 480,
    role: 'Database Administrator',
    permissions: ['read', 'write', 'admin'],
    expertiseAreas: ['SQL Server', 'PostgreSQL', 'Performance Tuning'],
    certifications: ['Microsoft Certified: Azure Database Administrator', 'PostgreSQL Certified Professional'],
    workingHours: {
      start: '09:00',
      end: '17:00',
      timezone: 'America/Los_Angeles',
      workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    },
    highContrast: false,
    fontSize: 'medium',
    reducedMotion: false,
    screenReader: false
  });

  const [isEditing, setIsEditing] = useState(false);

  const handleSave = (profile: UserProfile) => {
    setCurrentProfile(profile);
    setIsEditing(false);

    // Here you would typically save to your backend
    console.log('Saving user profile:', profile);

    // Show success message
    alert('Profile updated successfully!');
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className="container mx-auto p-6">
        <UserProfileManager
          profile={currentProfile}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Profile & Preferences</h1>
          <p className="text-gray-600 mt-2">
            Manage your personal settings, preferences, and account security separate from client configurations.
          </p>
        </div>
        <Button onClick={handleEdit} className="bg-shiftleft-600 hover:bg-shiftleft-700">
          Edit Profile
        </Button>
      </div>

      {/* Profile Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </CardTitle>
            <CardDescription>
              Your basic profile information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              {currentProfile.avatarBase64 ? (
                <img
                  src={currentProfile.avatarBase64}
                  alt="Profile"
                  className="w-12 h-12 rounded-full object-cover border"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-shiftleft-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-shiftleft-600" />
                </div>
              )}
              <div>
                <div className="font-semibold">{currentProfile.firstName} {currentProfile.lastName}</div>
                <div className="text-sm text-gray-600">{currentProfile.title}</div>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Email:</span> {currentProfile.email}
              </div>
              {currentProfile.phone && (
                <div>
                  <span className="font-medium">Phone:</span> {currentProfile.phone}
                </div>
              )}
              <div>
                <span className="font-medium">Department:</span> {currentProfile.department}
              </div>
              <div>
                <span className="font-medium">Role:</span> {currentProfile.role}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              System Preferences
            </CardTitle>
            <CardDescription>
              Your UI and system settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Theme:</span>
              <span className="ml-2 capitalize">{currentProfile.theme}</span>
            </div>
            <div>
              <span className="font-medium">Language:</span>
              <span className="ml-2">English</span>
            </div>
            <div>
              <span className="font-medium">Timezone:</span>
              <span className="ml-2">{currentProfile.timezone}</span>
            </div>
            <div>
              <span className="font-medium">Date Format:</span>
              <span className="ml-2">{currentProfile.dateFormat}</span>
            </div>
            <div>
              <span className="font-medium">Time Format:</span>
              <span className="ml-2">{currentProfile.timeFormat}</span>
            </div>
            <div>
              <span className="font-medium">Items per page:</span>
              <span className="ml-2">{currentProfile.itemsPerPage}</span>
            </div>
            <div>
              <span className="font-medium">Font size:</span>
              <span className="ml-2 capitalize">{currentProfile.fontSize}</span>
            </div>
          </CardContent>
        </Card>

        {/* Security & Access */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security & Access
            </CardTitle>
            <CardDescription>
              Your account security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Two-Factor Auth:</span>
              <span className={`ml-2 ${currentProfile.twoFactorEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {currentProfile.twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="font-medium">Session Timeout:</span>
              <span className="ml-2">{currentProfile.sessionTimeout} minutes</span>
            </div>
            <div>
              <span className="font-medium">Permissions:</span>
              <div className="ml-2 mt-1 flex flex-wrap gap-1">
                {currentProfile.permissions.map(permission => (
                  <span key={permission} className="px-2 py-1 bg-shiftleft-100 text-shiftleft-800 text-xs rounded">
                    {permission}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="font-medium">Working Hours:</span>
              <div className="ml-2 text-xs">
                {currentProfile.workingHours.start} - {currentProfile.workingHours.end}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expertise and Certifications */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Expertise Areas</CardTitle>
            <CardDescription>
              Your technical skills and areas of expertise
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {currentProfile.expertiseAreas.map(area => (
                <span key={area} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  {area}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Certifications</CardTitle>
            <CardDescription>
              Your professional certifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {currentProfile.certifications.map(cert => (
                <div key={cert} className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-shiftleft-600 rounded-full"></span>
                  <span className="text-sm">{cert}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            How you prefer to receive notifications and updates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.emailNotifications ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Email Notifications</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.pushNotifications ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Push Notifications</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.smsNotifications ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>SMS Notifications</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.weeklyReports ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Weekly Reports</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.criticalAlerts ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Critical Alerts</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${currentProfile.maintenanceNotifications ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Maintenance Notifications</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Important Notice */}
      <Card className="border-shiftleft-200 bg-shiftleft-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-shiftleft-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">i</span>
            </div>
            <div>
              <h3 className="font-semibold text-shiftleft-900 mb-1">User vs Client Profiling</h3>
              <p className="text-shiftleft-800 text-sm">
                This page manages your personal user account settings and preferences.
                Client/customer profiling and configuration is managed separately in the
                <strong> Client Configuration</strong> section, following enterprise best practices
                like Microsoft Dynamics and Oracle systems where user preferences are distinct
                from customer/client data management.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserProfilePage;
